/**
 * Builder.io Content Hook
 * 
 * Fetches Builder "page" model content for a given pathname.
 * Returns null if disabled, not found, or on error (enables fallback to code).
 */

import { useState, useEffect } from 'react';
import { getBuilder, isBuilderEnabled } from './config';

interface BuilderContent {
  id: string;
  data: any;
}

// Simple in-memory cache keyed by pathname
const contentCache = new Map<string, BuilderContent | null>();
const fetchPromises = new Map<string, Promise<BuilderContent | null>>();

export function useBuilderContent(pathname: string): BuilderContent | null {
  const [content, setContent] = useState<BuilderContent | null>(
    contentCache.get(pathname) || null
  );

  useEffect(() => {
    // Early return if disabled
    if (!isBuilderEnabled()) {
      return;
    }

    // Return cached content immediately
    const cached = contentCache.get(pathname);
    if (cached !== undefined) {
      setContent(cached);
      return;
    }

    // Avoid duplicate fetches for the same pathname
    const existingPromise = fetchPromises.get(pathname);
    if (existingPromise) {
      existingPromise.then(setContent);
      return;
    }

    // Fetch Builder content
    const fetchContent = async () => {
      try {
        const builder = await getBuilder();
        if (!builder) {
          contentCache.set(pathname, null);
          setContent(null);
          return;
        }

        const result = await builder.get('page', {
          url: pathname,
          options: {
            noTargeting: true,
          },
        });

        const builderContent: BuilderContent | null = result ? {
          id: result.id,
          data: result,
        } : null;

        contentCache.set(pathname, builderContent);
        fetchPromises.delete(pathname);
        setContent(builderContent);
      } catch (error) {
        // Silently fail - Builder is optional, fallback to code rendering
        if (import.meta.env.DEV) {
          console.warn(`[Builder] Failed to fetch content for ${pathname} (non-blocking):`, error);
        }
        contentCache.set(pathname, null);
        fetchPromises.delete(pathname);
        setContent(null);
      }
    };

    const promise = fetchContent();
    fetchPromises.set(pathname, promise);
  }, [pathname]);

  return content;
}

